// LearnCore Frontend - 登录页面

import React, { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { InlineLoading } from '../components/Loading';
import { EyeIcon, EyeSlashIcon } from '@heroicons/react/24/outline';

export default function LoginPage() {
  const { login, loading, error, clearError } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  
  // 获取 redirect 参数，用于跨应用跳转（如 TapeDeck）
  const redirectUrl = searchParams.get('redirect');
  
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  
  // 添加调试日志
  console.log('[LoginPage] 组件渲染，当前表单数据:', formData);
  const [showPassword, setShowPassword] = useState(false);
  const [formError, setFormError] = useState('');
  const [loginMode, setLoginMode] = useState<'email' | 'username'>('email');

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // 清除错误信息
    if (error) clearError();
    if (formError) setFormError('');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // 添加调试日志
    console.log('[LoginPage] 提交登录表单，数据:', formData);
    console.log('[LoginPage] 登录模式:', loginMode);
    
    // 前端验证
    if (!formData.email || !formData.password) {
      console.log('[LoginPage] 验证失败: 邮箱/用户名或密码为空');
      setFormError(loginMode === 'email' ? '邮箱和密码不能为空' : '用户名和密码不能为空');
      return;
    }

    // 根据登录模式进行验证
    if (loginMode === 'email') {
      // 邮箱格式验证
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
        console.log('[LoginPage] 验证失败: 邮箱格式不正确');
        setFormError('请输入有效的邮箱地址');
        return;
      }
    } else {
      // 用户名格式验证
      const usernameRegex = /^[a-zA-Z_][a-zA-Z0-9_]{2,19}$/;
      if (!usernameRegex.test(formData.email)) {
        console.log('[LoginPage] 验证失败: 用户名格式不正确');
        setFormError('用户名格式不正确（3-20个字符，字母数字下划线，不能以数字开头）');
        return;
      }
    }

    // 构建登录请求数据
    const loginData = loginMode === 'email'
      ? { email: formData.email, password: formData.password }
      : { username: formData.email, password: formData.password };

    try {
      console.log('[LoginPage] 调用登录API...', loginData);
      await login(loginData);
      console.log('[LoginPage] 登录成功');
      
      // 如果有 redirect 参数（来自其他应用如 TapeDeck），跳转到该 URL
      if (redirectUrl) {
        // 安全检查：只允许跳转到 mindcraft.win 域名
        try {
          const url = new URL(redirectUrl);
          if (url.hostname.endsWith('.mindcraft.win') || url.hostname === 'mindcraft.win') {
            console.log('[LoginPage] 跳转到外部应用:', redirectUrl);
            window.location.href = redirectUrl;
            return;
          }
        } catch {
          // URL 解析失败，忽略 redirect 参数
        }
      }
      
      // 默认跳转到仪表板
      console.log('[LoginPage] 跳转到仪表板');
      navigate('/dashboard');
    } catch (err) {
      console.error('[LoginPage] 登录失败:', err);
      // 错误已在AuthContext中处理
    }
  };

  const displayError = error || formError;

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div>
          <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
            {loginMode === 'email' ? '家长登录' : '孩子登录'}
          </h2>
          <p className="mt-2 text-center text-sm text-gray-600">
            {loginMode === 'email' ? '家长使用邮箱登录' : '孩子使用用户名登录'}
            {loginMode === 'email' && (
              <>
                {' '}还没有账户？{' '}
                <Link
                  to="/register"
                  className="font-medium text-primary-600 hover:text-primary-500 transition-colors"
                >
                  立即注册
                </Link>
              </>
            )}
          </p>
        </div>

        <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
          {displayError && (
            <div className="alert-danger">
              {displayError}
            </div>
          )}

          <div className="space-y-4">
            <div>
              <div className="flex items-center justify-between mb-2">
                <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                  {loginMode === 'email' ? '邮箱地址' : '用户名'}
                </label>
                <button
                  type="button"
                  onClick={() => setLoginMode(loginMode === 'email' ? 'username' : 'email')}
                  className="text-sm text-primary-600 hover:text-primary-500"
                >
                  {loginMode === 'email' ? '使用用户名登录' : '使用邮箱登录'}
                </button>
              </div>
              <input
                id="email"
                name="email"
                type={loginMode === 'email' ? 'email' : 'text'}
                autoComplete={loginMode === 'email' ? 'email' : 'username'}
                required
                className="input"
                placeholder={loginMode === 'email' ? '请输入邮箱地址' : '请输入用户名'}
                value={formData.email}
                onChange={handleChange}
                disabled={loading}
              />
            </div>

            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-1">
                密码
              </label>
              <div className="relative">
                <input
                  id="password"
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  autoComplete="current-password"
                  required
                  className="input pr-10"
                  placeholder="请输入密码"
                  value={formData.password}
                  onChange={handleChange}
                  disabled={loading}
                />
                <button
                  type="button"
                  className="absolute inset-y-0 right-0 pr-3 flex items-center"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? (
                    <EyeSlashIcon className="h-5 w-5 text-gray-400 hover:text-gray-600" />
                  ) : (
                    <EyeIcon className="h-5 w-5 text-gray-400 hover:text-gray-600" />
                  )}
                </button>
              </div>
            </div>
          </div>

          <div>
            <button
              type="submit"
              className="w-full btn-primary py-3 text-base"
              disabled={loading}
            >
              {loading ? (
                <div className="flex items-center justify-center space-x-2">
                  <InlineLoading size="sm" />
                  <span>登录中...</span>
                </div>
              ) : (
                '登录'
              )}
            </button>
          </div>

          <div className="text-center space-y-2">
            <Link
              to="/"
              className="text-sm text-gray-600 hover:text-primary-600 transition-colors"
            >
              返回首页
            </Link>
            {loginMode === 'email' && (
              <div>
                <Link
                  to="/child/login"
                  className="text-sm text-green-600 hover:text-green-500 transition-colors"
                >
                  孩子专用登录入口
                </Link>
              </div>
            )}
          </div>
        </form>
      </div>
    </div>
  );
}
